import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useHotel } from '@/context/HotelContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Users, Search, Star, Pencil, Trash2, RefreshCw } from 'lucide-react';
import { getErrorMessage } from '@/lib/errorUtils';

import { API } from '@/config/api';
const AUTO_REFRESH_INTERVAL = 15000;

const GuestsPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const { hotelSettings } = useHotel();
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingGuest, setEditingGuest] = useState(null);
  
  // Get guest field config with defaults
  const guestFields = hotelSettings?.guest_fields || {
    email: true,
    phone: true,
    address: true,
    nationality: true,
    id_type: true,
    id_number: true,
    notes: true
  };
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    id_type: '',
    id_number: '',
    nationality: '',
    address: '',
    notes: '',
    is_vip: false
  });

  const fetchGuests = useCallback(async (searchQuery = '', showIndicator = true) => {
    if (!user?.hotel_id) return;
    if (showIndicator) setLoading(true);
    try {
      const params = searchQuery ? `?search=${encodeURIComponent(searchQuery)}` : '';
      const response = await axios.get(`${API}/hotels/${user.hotel_id}/guests${params}`, {
        headers: getAuthHeaders()
      });
      setGuests(response.data);
    } catch (error) {
      console.error('Failed to fetch guests:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [user?.hotel_id, getAuthHeaders]);

  // Debounce search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchGuests(searchTerm, true);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, fetchGuests]);

  // Auto-refresh every 15 seconds
  useEffect(() => {
    const intervalId = setInterval(() => fetchGuests(searchTerm, false), AUTO_REFRESH_INTERVAL);
    return () => clearInterval(intervalId);
  }, [fetchGuests, searchTerm]);

  // Initial fetch
  useEffect(() => {
    if (user?.hotel_id) {
      fetchGuests();
    }
  }, [user?.hotel_id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingGuest) {
        const response = await axios.put(
          `${API}/hotels/${user.hotel_id}/guests/${editingGuest.id}`,
          formData,
          { headers: getAuthHeaders() }
        );
        setGuests(prev => prev.map(g => g.id === editingGuest.id ? response.data : g));
        toast.success('Guest updated successfully');
      } else {
        const response = await axios.post(
          `${API}/hotels/${user.hotel_id}/guests`,
          formData,
          { headers: getAuthHeaders() }
        );
        setGuests(prev => [response.data, ...prev]);
        toast.success('Guest added successfully');
      }
      setShowDialog(false);
      resetForm();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save guest'));
    }
  };

  const handleEdit = (guest) => {
    setEditingGuest(guest);
    setFormData({
      first_name: guest.first_name || '',
      last_name: guest.last_name || '',
      email: guest.email || '',
      phone: guest.phone || '',
      id_type: guest.id_type || '',
      id_number: guest.id_number || '',
      nationality: guest.nationality || '',
      address: guest.address || '',
      notes: guest.notes || '',
      is_vip: guest.is_vip || false
    });
    setShowDialog(true);
  };

  const handleDelete = async (guestId) => {
    if (!window.confirm('Delete this guest? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/hotels/${user.hotel_id}/guests/${guestId}`, {
        headers: getAuthHeaders()
      });
      setGuests(prev => prev.filter(g => g.id !== guestId));
      toast.success('Guest deleted');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete guest'));
    }
  };

  const resetForm = () => {
    setEditingGuest(null);
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      id_type: '',
      id_number: '',
      nationality: '',
      address: '',
      notes: '',
      is_vip: false
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="guests-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold font-['Outfit']">Guests</h1>
          <p className="text-sm text-muted-foreground">Manage guest profiles</p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <Button variant="outline" size="sm" onClick={() => { setRefreshing(true); fetchGuests(searchTerm, false); }} disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 sm:mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
          <Dialog open={showDialog} onOpenChange={(open) => {
            setShowDialog(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button data-testid="add-guest-btn" size="sm" className="sm:size-default">
                <Plus className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Add Guest</span>
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-lg mx-2 sm:mx-auto">
            <DialogHeader>
              <DialogTitle>{editingGuest ? 'Edit Guest' : 'Add New Guest'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>First Name *</Label>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    placeholder="John"
                    data-testid="guest-firstname-input"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Last Name *</Label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    placeholder="Doe"
                    data-testid="guest-lastname-input"
                    required
                  />
                </div>
              </div>

              {(guestFields.email || guestFields.phone) && (
                <div className="grid grid-cols-2 gap-4">
                  {guestFields.email && (
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="john@example.com"
                        data-testid="guest-email-input"
                      />
                    </div>
                  )}
                  {guestFields.phone && (
                    <div className="space-y-2">
                      <Label>Phone</Label>
                      <Input
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="+33 6 12 34 56 78"
                        data-testid="guest-phone-input"
                      />
                    </div>
                  )}
                </div>
              )}

              {(guestFields.id_type || guestFields.id_number) && (
                <div className="grid grid-cols-2 gap-4">
                  {guestFields.id_type && (
                    <div className="space-y-2">
                      <Label>ID Type</Label>
                      <Input
                        value={formData.id_type}
                        onChange={(e) => setFormData({ ...formData, id_type: e.target.value })}
                        placeholder="Passport / ID Card"
                        data-testid="guest-idtype-input"
                      />
                    </div>
                  )}
                  {guestFields.id_number && (
                    <div className="space-y-2">
                      <Label>ID Number</Label>
                      <Input
                        value={formData.id_number}
                        onChange={(e) => setFormData({ ...formData, id_number: e.target.value })}
                        placeholder="AB123456"
                        data-testid="guest-idnumber-input"
                      />
                    </div>
                  )}
                </div>
              )}

              {(guestFields.nationality || guestFields.address) && (
                <div className="grid grid-cols-2 gap-4">
                  {guestFields.nationality && (
                    <div className="space-y-2">
                      <Label>Nationality</Label>
                      <Input
                        value={formData.nationality}
                        onChange={(e) => setFormData({ ...formData, nationality: e.target.value })}
                        placeholder="French"
                        data-testid="guest-nationality-input"
                      />
                    </div>
                  )}
                  {guestFields.address && (
                    <div className="space-y-2">
                      <Label>Address</Label>
                      <Input
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        placeholder="123 Main St, Paris"
                        data-testid="guest-address-input"
                      />
                    </div>
                  )}
                </div>
              )}

              {guestFields.notes && (
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Input
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Special requests, preferences, etc."
                    data-testid="guest-notes-input"
                  />
                </div>
              )}

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="vip"
                  checked={formData.is_vip}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_vip: checked })}
                  data-testid="guest-vip-checkbox"
                />
                <Label htmlFor="vip" className="flex items-center gap-1">
                  <Star className="h-4 w-4 text-amber-500" />
                  VIP Guest
                </Label>
              </div>

              <Button type="submit" className="w-full" data-testid="submit-guest-btn">
                {editingGuest ? 'Update Guest' : 'Add Guest'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search guests..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="guest-search-input"
        />
      </div>

      {/* Guest List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {guests.map((guest) => (
          <Card key={guest.id} className="card-interactive" data-testid={`guest-card-${guest.id}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-950 flex items-center justify-center">
                    <span className="text-sm font-medium text-blue-600">
                      {guest.first_name?.[0]}{guest.last_name?.[0]}
                    </span>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold">{guest.first_name} {guest.last_name}</h3>
                      {guest.is_vip && (
                        <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{guest.email || 'No email'}</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleEdit(guest)}
                    data-testid={`edit-guest-${guest.id}`}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(guest.id)}
                    data-testid={`delete-guest-${guest.id}`}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </div>

              {guest.phone && (
                <p className="text-sm text-muted-foreground mb-2">📞 {guest.phone}</p>
              )}

              {guest.nationality && (
                <p className="text-sm text-muted-foreground mb-2">🌍 {guest.nationality}</p>
              )}

              <div className="flex items-center gap-4 mt-3 pt-3 border-t text-sm">
                <div>
                  <span className="text-muted-foreground">Stays: </span>
                  <span className="font-medium">{guest.total_stays || 0}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Total: </span>
                  <span className="font-medium">€{(guest.total_spent || 0).toFixed(2)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {guests.length === 0 && !loading && (
        <Card>
          <CardContent className="py-8 text-center">
            <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              {searchTerm ? 'No guests found matching your search.' : 'No guests yet. Add your first guest.'}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default GuestsPage;
